<template>
  <div class="custom_tab" :class="[width, `variant-${variant}`]">
    <div
      :class="[
        'tab_headers',
        extraHeaderStyles,
        orientation === 'col' ? 'orientation-col' : '',
      ]"
    >
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :class="[
          'tab_header_container',
          orientation === 'col' ? 'orientation-col' : '',
        ]"
      >
        <div
          :class="[
            'tab_header',
            'inline-block',
            { active: index === activeTab, disabled: tab.disabled === true },
            orientation === 'col' ? 'orientation-col' : '',
            `size-${type}`,
            tabHeaderClass,
          ]"
          @click="tab.disabled !== true && selectTab(tab, index)"
          :ref="
            (el) => {
              if (el) tabHeaderRefs[index] = el;
            }
          "
        >
          <ThemeImage
            v-if="tab.selected && index === activeTab"
            class="size-4 flex-shrink-0"
            :icon="tab.selected"
            alt="icon"
          />

          <ThemeImage
            v-if="tab.unselected && index !== activeTab"
            class="size-4 flex-shrink-0"
            :icon="tab.unselected"
            alt="icon"
          />

          <span class="tab_title" :title="tab.title">{{ tab.title }}</span>

          <a-tooltip
            placement="right"
            v-if="tab.disabled && tab.tooltip"
            :title="tab.tooltip"
          >
            <ThemeImage
              :icon="imageNames.INFO"
              alt="info_icon"
              class="size-4 flex-shrink-0"
            />
          </a-tooltip>
          <span
            v-if="tab.count !== undefined"
            class="tab_count flex-shrink-0"
            >{{ tab.count }}</span
          >
        </div>
        <div
          v-if="tab && tab.header"
          class="tab_header_below"
          :ref="
            (el) => {
              if (el) headerBelowRefs[index] = el;
            }
          "
        >
          <component :is="tab.header" />
        </div>
      </div>

      <div :class="['additional_slot', { 'no-auto-margin': isCentered }]">
        <slot :name="`additional-${activeTab}`"></slot>
        <slot name="global"></slot>
      </div>
    </div>

    <div
      :class="['overflow-y-auto', contentPadding]"
      :style="{ height: height, maxHeight: height }"
      v-if="showTransition"
    >
      <transition :name="transitionName" mode="out-in">
        <div :key="activeTab" class="tab-pane">
          <slot :name="`tab_content-${activeTab}`"></slot>
        </div>
      </transition>
    </div>
    <div v-if="mountAllTabsInitially">
      <div v-for="(tab, index) in tabs" :key="`all-${index}`" v-show="false">
        <slot :name="`tab_content-${index}`"></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { variants } from "@/src/constants/customComponentConstants.js";

const props = defineProps({
  initialActiveTab: { type: Number, default: 0 },
  tabs: { type: Array, required: true },
  modelValue: { type: [Number, String], required: true },
  showTransition: { type: Boolean, default: true, required: false },
  width: { type: String, default: "" },
  height: { type: String, required: false },
  mountAllTabsInitially: { type: Boolean, default: false, required: false },
  contentPadding: { type: String, default: "pt-5" },
  extraHeaderStyles: { type: String, default: "overflow-x-scroll" },
  tabHeaderClass: { type: String, default: "min-w-[60px] max-w-[150px]" },
  orientation: {
    type: String,
    default: "row",
    validator: (value) => ["row", "col"].includes(value),
  },
  variant: {
    type: String,
    default: variants.VARIANT1,
    validator: (value) => Object.values(variants).includes(value),
  },
  type: {
    type: String,
    default: "medium",
    validator: (value) => ["medium", "large"].includes(value),
  },
});

const emit = defineEmits(["tab-selected", "update:modelValue"]);
const activeTab = ref(props.initialActiveTab);
const transitionName = ref("slide");
const tabHeaderRefs = ref([]);
const headerBelowRefs = ref([]);

const isCentered = computed(() => {
  const styles = props.extraHeaderStyles || "";
  return (
    styles.includes("justify-center") ||
    styles.includes("justify-around") ||
    styles.includes("justify-evenly")
  );
});

const findTabIndex = (value) => {
  if (typeof value === "string") {
    const tabIndex = props.tabs.findIndex((tab) => tab.id === value);
    return tabIndex !== -1 ? tabIndex : 0;
  }
  return value;
};

activeTab.value = findTabIndex(props.modelValue);

watch(
  () => props.modelValue,
  (newVal) => {
    activeTab.value = findTabIndex(newVal);
  },
  { immediate: true },
);

const selectTab = (tab, index) => {
  transitionName.value = index > activeTab.value ? "slide-right" : "slide-left";
  activeTab.value = index;

  const emitValue =
    typeof props.modelValue === "string" && tab.id ? tab.id : index;
  emit("update:modelValue", emitValue);
  emit("tab-selected", tab.title);
};

onMounted(() => {
  if (props.mountAllTabsInitially) {
    for (let i = 0; i < props.tabs.length; i++) {
      activeTab.value = i;
    }
    activeTab.value = props.initialActiveTab;
  }
});
</script>

<style scoped>
.custom_tab {
  color: var(--foreground-rgb);
  border-radius: 4px;
  overflow: visible;
}

.tab_headers {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
}

.tab_headers.orientation-col {
  flex-direction: column;
  align-items: stretch;
  padding: 0 12px;
  width: 100%;
  overflow-x: hidden;
}

.variant-variant2 .tab_headers {
  position: relative;
}

.tab_header_container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  position: relative;
}

.tab_header_container.orientation-col {
  align-items: stretch;
}

.tab_header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition:
    background-color 0.3s,
    color 0.3s;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Proxima Nova", sans-serif;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  color: var(--secondary-color);
}

.tab_header.size-medium {
  padding: 6px 10px;
}

.tab_header.size-large {
  padding: 10px 12px;
}

.tab_title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.variant-variant2 .tab_header {
  border-radius: 24px;
  color: var(--color-666);
}

.variant-variant2 .tab_header.size-medium {
  padding: 6px 10px;
}

.variant-variant2 .tab_header.size-large {
  padding: 10px 12px;
}

.tab_header.orientation-col {
  height: 40px;
  width: 100%;
  justify-content: flex-start;
}

.tab_header.active {
  background: var(--white-opacity-6);
  color: var(--foreground-rgb);
  font-weight: 600;
}

.variant-variant2 .tab_header.active {
  background: linear-gradient(
    90deg,
    rgba(179, 67, 203, 0.24) 0%,
    rgba(112, 149, 235, 0.24) 100%
  );
  color: var(--foreground-rgb);
  font-weight: 600;
}

.variant-variant3 .tab_headers {
  border-bottom: 1px solid var(--white-opacity-8);
  gap: 8px;
  overflow: visible !important;
}

.variant-variant3 .tab_header_container {
  align-self: flex-end;
}

.variant-variant3 .tab_header {
  border-radius: 8px 8px 0 0;
  border: 1px solid transparent;
  color: var(--secondary-color);
  margin-bottom: -1px;
  position: relative;
}

.variant-variant3 .tab_header.active {
  background-color: var(--background-color);
  color: var(--foreground-rgb);
  font-weight: 600;
  border: 1px solid var(--white-opacity-10);
  border-bottom: 1px solid var(--background-color);
  z-index: 1;
}

.variant-variant3 .tab_header:hover {
  background-color: transparent;
  color: var(--foreground-rgb);
}

.variant-variant3 .additional_slot {
  padding: 8px 0;
}

.tab_header:hover {
  background-color: var(--white-opacity-10);
}

.tab_header.disabled {
  opacity: 60%;
  cursor: not-allowed;
}

.tab_header.disabled:hover {
  background-color: transparent;
}

.tab_header_below {
  position: absolute;
  top: 80%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  white-space: nowrap;
}

.tab_count {
  display: flex;
  min-width: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 24px;
  background: var(--white-opacity-14);
  color: var(--foreground-rgb);
  font-family: "Proxima Nova", sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  padding: 0 4px;
}

.slide-right-enter-active,
.slide-left-enter-active {
  transition: transform 0.2s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.slide-right-enter-to,
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-left-enter-from,
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-left-enter-to,
.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.additional_slot {
  margin-left: auto;
}

.additional_slot.no-auto-margin {
  margin-left: 0;
}

.count {
  background-color: #666;
  border-radius: 50%;
  padding: 2px 8px;
  margin-left: 5px;
  font-size: 0.9em;
  color: var(--foreground-rgb);
}
</style>
